import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";

import api from "../../../../../services/api";
import { notify } from "../../../../../services/toast.config";
import { showBasicAlert } from "../../../../../core/common/useAlert";
import { getDefaultForm } from "../../../../../core/common/formSelection";

import type {
  CompanyChoice,
  FormChoice,
  SelectOption,
  UserOption,
} from "../types";

export const useLeadsTransfer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const previousCompany = location.state?.companyDetails || null;
  const previousForm = location.state?.formDetails || null;
  const signature = localStorage.getItem("signature");

  const [isLoading, setIsLoading] = useState(false);

  const [companies, setCompanies] = useState<any[]>([]);
  const [companyChoose, setCompanyChoose] = useState<CompanyChoice>({
    signature,
    companyName: "Select Company",
    companyId: "",
    companyType: "Leads",
  });
  const [forms, setForms] = useState<any[]>([]);
  const [formChoose, setFormChoose] = useState<FormChoice>({
    signature,
    pipeline_id: "Select Form",
    form_name: "",
    form_id: "",
  });
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  const [leads, setLeads] = useState<any[]>([]);
  const [transferHistory, setTransferHistory] = useState<any[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [fromUserId, setFromUserId] = useState<number | null>(null);
  const [toUserId, setToUserId] = useState<number | null>(null);
  const [transferring, setTransferring] = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState<Dayjs | null>(dayjs().subtract(1, "month"));
  const [exportEndDate, setExportEndDate] = useState<Dayjs | null>(dayjs());
  const [exportTransferFrom, setExportTransferFrom] = useState<number | undefined>(undefined);
  const [exportTransferTo, setExportTransferTo] = useState<number | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState(false);

  const autoCompanyApplied = useRef(false);

  // ---- helpers -------------------------------------------------------------
  const filterUsersByForm = (formId: string, formList: any[], allUsers: any[]) => {
    const selectedForm = formList.find(
      (f) => f.key === parseInt(formId) || f.id === parseInt(formId),
    );
    if (selectedForm?.employees?.length > 0) {
      setFilteredUsers(allUsers.filter((u) => selectedForm.employees.includes(u.key)));
    } else {
      setFilteredUsers(allUsers);
    }
  };

  const applyCompany = async (companyId: string, companyName: string, companyType: string) => {
    if (!companyId || !companyType) return;
    setCompanyChoose((prev) => ({ ...prev, companyId, companyName, companyType }));
    try {
      const [formsRes, settingsRes] = await Promise.all([
        api.post("leadService/getLeadFormsByCompany", {
          signature,
          company_id: companyId,
          company_type: companyType,
        }),
        api.post("leadService/getAllLeadSettings", {
          signature,
          company_id: parseInt(companyId),
          company_type: companyType,
        }),
      ]);

      const userRes = await api.post("userService/getLeadEmployeesListData", {
        signature,
        department:
          settingsRes?.data?.data?.data?.[0]?.settings?.lead_assigned_to_department || -1,
        company_id: parseInt(companyId),
        company_type: companyType,
      });

      const formsWithEmployees = (formsRes.data?.data?.data || []).map((form: any) => ({
        ...form,
        employees: form.employees || [],
      }));
      const allUsers = userRes.data?.data?.data?.allUsers || [];
      setForms(formsWithEmployees);
      setUsers(allUsers);
      if (formChoose.form_id) {
        filterUsersByForm(formChoose.form_id, formsWithEmployees, allUsers);
      }
    } catch (err) {
      console.error("Error loading company data:", err);
    }
  };

  const applyForm = async (pipeline_id: string, form_id: string, form_name: string) => {
    setFormChoose((prev) => ({ ...prev, pipeline_id, form_id, form_name }));
    if (!form_id) return;
    try {
      const pipelineRes = await api.post("leadService/getAllPipelinesById", {
        signature,
        id: pipeline_id,
      });
      setPipelines(pipelineRes.data?.data?.data || []);
      filterUsersByForm(form_id, forms, users);
      if (previousCompany || previousForm) {
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: { ...location.state, companyDetails: undefined, formDetails: undefined } },
        );
      }
    } catch (err) {
      console.error("Error loading pipelines:", err);
    }
  };

  // ---- effects -------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await api.post("userService/getAllCompanyList", { signature });
        setCompanies(res.data?.data?.data || []);
      } catch (err) {
        console.error("Error loading companies:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (autoCompanyApplied.current) return;
    if (previousCompany?.companyId && previousCompany?.companyType) {
      autoCompanyApplied.current = true;
      applyCompany(previousCompany.companyId, previousCompany.companyName, previousCompany.companyType);
    } else if (companies.length > 0) {
      autoCompanyApplied.current = true;
      const first = companies[0];
      applyCompany(first.id, first.name, first.type);
    }
  }, [companies]);

  useEffect(() => {
    if (!forms.length) return;
    if (previousForm?.form_id) {
      applyForm(previousForm.pipeline_id, previousForm.form_id, previousForm.form_name);
    } else {
      const def = getDefaultForm(forms);
      if (def) applyForm(String(def.pipeline_id), String(def.key), def.LeadFormName);
    }
  }, [forms]);

  useEffect(() => {
    const run = async () => {
      if (!companyChoose.companyId || pipelines.length === 0 || !formChoose.form_id) {
        setLeads([]);
        setTransferHistory([]);
        return;
      }
      try {
        setIsLoading(true);
        const [leadsRes, historyRes] = await Promise.all([
          api.post("leadService/getLeadsForTransfer", {
            signature,
            company_id: companyChoose.companyId,
            company_type: companyChoose.companyType,
            form_id: formChoose.form_id,
            pipeline_id: parseInt(formChoose.pipeline_id),
          }),
          api.post("leadService/getTransferLeads", {
            signature,
            company_id: companyChoose.companyId,
            company_type: companyChoose.companyType,
            form_id: formChoose.form_id,
          }),
        ]);
        setLeads(leadsRes.data?.data?.data || []);
        setTransferHistory(historyRes.data?.data?.data || []);
      } catch (err) {
        console.error("Error loading transfer data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [formChoose.form_id, formChoose.pipeline_id, pipelines, companyChoose.companyId, refreshTick]);

  // ---- derived -------------------------------------------------------------
  const companyOptions: SelectOption[] = useMemo(
    () => companies.map((c) => ({ value: `${c.id}-${c.type}`, label: `${c.name} - ${c.type}` })),
    [companies],
  );

  const companyValue =
    companyChoose.companyId && companyChoose.companyType
      ? `${companyChoose.companyId}-${companyChoose.companyType}`
      : undefined;

  const formOptions: SelectOption[] = useMemo(
    () => forms.map((f) => ({ value: `${f.pipeline_id}-${f.key}`, label: f.LeadFormName })),
    [forms],
  );

  const formValue = formChoose.form_id
    ? `${formChoose.pipeline_id}-${formChoose.form_id}`
    : undefined;

  const userOptions: UserOption[] = useMemo(
    () =>
      filteredUsers.map((u) => ({
        value: u.key,
        label: u.Name || `${u.Fname ?? ""} ${u.Lname ?? ""}`.trim(),
        image: u.Image,
      })),
    [filteredUsers],
  );

  const employeeOptions = useMemo(
    () =>
      users.map((u) => ({
        label: u.Name || `${u.Fname} ${u.Lname}`,
        value: u.key?.toString(),
        key: u.key,
        image_url: u.Image || "assets/img/users/user.png",
      })),
    [users],
  );

  const baseLeads = useMemo(
    () => (fromUserId ? leads.filter((l) => l.assigned_to === fromUserId) : []),
    [leads, fromUserId],
  );

  // ---- handlers ------------------------------------------------------------
  const onCompanyChange = (value: string | number) => {
    const match = companies.find((c) => `${c.id}-${c.type}` === value);
    if (match) applyCompany(match.id, match.name, match.type);
  };

  const onFormChange = (value: string) => {
    if (!value) return;
    const [pipeline_id, form_id] = value.split("-");
    const label = formOptions.find((o) => o.value === value)?.label || "";
    applyForm(pipeline_id, form_id, label);
  };

  const openTransferModal = () => setShowTransferModal(true);
  const closeTransferModal = () => {
    setShowTransferModal(false);
    setFromUserId(null);
    setToUserId(null);
  };

  const onTransfer = (selectedKeys: number[]) => {
    if (!fromUserId || !toUserId) {
      showBasicAlert("Please select both users.");
      return;
    }
    if (fromUserId === toUserId) {
      showBasicAlert("Cannot transfer to the same user.");
      return;
    }
    if (selectedKeys.length === 0) {
      showBasicAlert("Please select leads for transfer");
      return;
    }
    Modal.confirm({
      title: "Confirm Lead Transfer",
      content: `Are you sure you want to transfer ${selectedKeys.length} lead(s)?`,
      okText: "Yes, Transfer",
      cancelText: "Cancel",
      centered: true,
      zIndex: 1100,
      onOk: async () => {
        try {
          setTransferring(true);
          await api.post("leadService/transferLeads", {
            signature,
            from_user_id: fromUserId,
            to_user_id: toUserId,
            company_id: companyChoose.companyId,
            company_type: companyChoose.companyType,
            form_id: formChoose.form_id,
            selectedLeads: selectedKeys,
          });
          notify.success("Leads transferred successfully!");
          closeTransferModal();
          setRefreshTick((t) => t + 1);
        } catch (err) {
          notify.error("Transfer failed");
        } finally {
          setTransferring(false);
        }
      },
    });
  };

  const openExportModal = () => setShowExportModal(true);
  const closeExportModal = () => {
    setShowExportModal(false);
    setExportStartDate(dayjs().subtract(1, "month"));
    setExportEndDate(dayjs());
    setExportTransferFrom(undefined);
    setExportTransferTo(undefined);
  };

  const handleExportTransferList = async () => {
    try {
      if (!companyChoose.companyId || !companyChoose.companyType) {
        notify.error("Please select a company first");
        return;
      }
      if (!formChoose.form_id) {
        notify.error("Please select form first");
        return;
      }
      if (!exportStartDate || !exportEndDate) {
        notify.error("Please select both start and end dates");
        return;
      }
      if (exportStartDate.isAfter(exportEndDate)) {
        notify.error("Start date cannot be after end date");
        return;
      }
      if (!exportTransferFrom || !exportTransferTo) {
        notify.error("Please select both Transfer From and Transfer To");
        return;
      }
      notify.success("Download Started");
      setExportLoading(true);
      const blob: any = await api.post(
        "leadService/exportLeadTransferData",
        {
          signature,
          company_id: companyChoose.companyId,
          company_type: companyChoose.companyType,
          start_date: exportStartDate.format("YYYY-MM-DD"),
          end_date: exportEndDate.format("YYYY-MM-DD"),
          form_id: formChoose.form_id,
          transfer_from: exportTransferFrom,
          transfer_to: exportTransferTo,
        },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([blob.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Lead_Transfer_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      notify.error("Download Failed");
    } finally {
      setExportLoading(false);
    }
  };

  return {
    isLoading,
    formName: formChoose.form_name,
    companyId: companyChoose.companyId,
    formId: formChoose.form_id,

    header: {
      formOptions,
      formValue,
      onFormChange,
      companyOptions,
      companyValue,
      onCompanyChange,
      transferDisabled: !formChoose.form_id,
      exportDisabled: isLoading || !companyChoose.companyId,
    },

    transferHistory,

    transferModal: {
      open: showTransferModal,
      onClose: closeTransferModal,
      userOptions,
      fromUserId,
      setFromUserId,
      toUserId,
      setToUserId,
      baseLeads,
      transferring,
      onTransfer,
    },

    openTransferModal,
    openExportModal,

    exportModal: {
      showExportModal,
      setShowExportModal,
      handleCloseExportModal: closeExportModal,
      exportStartDate,
      setExportStartDate,
      exportEndDate,
      setExportEndDate,
      exportLoading,
      handleExportTransferList,
      exportTransferFrom,
      exportTransferTo,
      setExportTransferFrom,
      setExportTransferTo,
      employeeOptions,
    },
  };
};

export default useLeadsTransfer;
